import { supabase } from '../lib/supabaseClient';
import { Client, WorkSite } from '@/types';
import type { ClientRow, WorkSiteRow } from '@/types/supabase';
import { logger } from '../utils/logger';
import { getOfflineCache, setOfflineCache } from '../lib/offlineCache';
import { isNetworkError } from '../utils/network';
import { isUniqueAddressSite, parseLegacyAddress } from '../utils/workSiteUtils';

export const mapWorkSiteRow = (ws: any): WorkSite => {
    const isUnique = Boolean(ws.is_unique_address || (ws.name && isUniqueAddressSite({ name: ws.name, isUniqueAddress: ws.is_unique_address } as any)));
    const hasStructured = Boolean(ws.street || ws.city || ws.province || ws.zip_code);
    const parsed = (!hasStructured && ws.address) ? parseLegacyAddress(ws.address) : {};

    return {
        id: ws.id,
        companyId: ws.company_id,
        name: ws.name,
        address: ws.address,
        isUniqueAddress: isUnique,
        locationDetails: {
            street: ws.street || parsed.street || null,
            number: ws.number || parsed.number || null,
            extra: ws.extra || parsed.extra || null,
            zipCode: ws.zip_code || parsed.zipCode || null,
            city: ws.city || parsed.city || null,
            province: ws.province || parsed.province || null
        }
    };
};

// Helper to map DB rows to our Frontend types
export const mapClientFromDB = (c: ClientRow): Client => ({
    id: c.id,
    companyId: c.company_id,
    name: c.name,
    email: c.email || '',
    phone: c.phone || '',
    createdAt: c.created_at,
    activity: c.activity_score,
    totalOrders: c.total_orders,
    workSites: c.work_sites?.map(mapWorkSiteRow) || []
});

export const ClientService = {
    async getClients(page: number = 1, limit: number = 50): Promise<{ clients: Client[], total: number }> {
        try {
            const from = (page - 1) * limit;
            const to = from + limit - 1;

            const { data, count, error } = await supabase
                .from('clients')
                .select(`
                    *,
                    work_sites (*)
                `, { count: 'exact' })
                .order('name')
                .range(from, to);

            if (error) throw error;

            const result = {
                clients: data.map(mapClientFromDB),
                total: count || 0
            };

            if (page === 1) {
                await setOfflineCache('pd_cached_clients', result);
            }
            return result;
        } catch (err) {
            if (isNetworkError(err)) {
                const cached = await getOfflineCache('pd_cached_clients');
                if (cached && page === 1) {
                    logger.info('ClientService: Restored clients from offline cache');
                    return cached;
                }
            }
            throw err;
        }
    },

    async searchClients(query: string): Promise<Client[]> {
        const { data, error } = await supabase
            .from('clients')
            .select(`*, work_sites (*)`)
            .ilike('name', `%${query}%`)
            .order('name')
            .limit(20);

        if (error) throw error;
        return (data || []).map(mapClientFromDB);
    },

    async createClient(client: Client): Promise<Client> {
        let companyId = client.companyId;
        if (!companyId) {
            const compRes = await supabase.rpc('get_my_company_id');
            companyId = compRes?.data || undefined;
        }

        // Validación de unicidad de nombre de cliente
        const { data: existing, error: searchError } = await supabase
            .from('clients')
            .select('id')
            .eq('company_id', companyId)
            .ilike('name', client.name.trim())
            .limit(1);
        
        if (!searchError && existing && existing.length > 0) {
            throw new Error(`Ya existe un cliente con el nombre "${client.name.trim()}" en esta empresa.`);
        }

        const { data: newClient, error } = await supabase.from('clients').insert({
            company_id: companyId,
            name: client.name,
            email: client.email,
            phone: client.phone
        }).select().single();

        if (error) throw error;

        let insertedWorkSites: WorkSite[] = [];
        if (client.workSites && client.workSites.length > 0) {
            const sitesToInsert = client.workSites.map(ws => {
                const isUnique = Boolean(ws.isUniqueAddress || isUniqueAddressSite(ws));
                const hasStructured = Boolean(ws.locationDetails?.street || ws.locationDetails?.city || ws.locationDetails?.province || ws.locationDetails?.zipCode);
                const parsed = (!hasStructured && ws.address) ? parseLegacyAddress(ws.address) : {};
                const loc = {
                    street: ws.locationDetails?.street || parsed.street || null,
                    number: ws.locationDetails?.number || parsed.number || null,
                    extra: ws.locationDetails?.extra || parsed.extra || null,
                    zipCode: ws.locationDetails?.zipCode || parsed.zipCode || null,
                    city: ws.locationDetails?.city || parsed.city || null,
                    province: ws.locationDetails?.province || parsed.province || null
                };

                return {
                    id: ws.id && !ws.id.startsWith('ws-') ? ws.id : undefined,
                    client_id: newClient.id,
                    company_id: companyId,
                    is_unique_address: isUnique,
                    name: ws.name,
                    address: ws.address,
                    street: loc.street,
                    number: loc.number,
                    extra: loc.extra,
                    zip_code: loc.zipCode,
                    city: loc.city,
                    province: loc.province,
                    location_details: loc
                };
            });

            const insertBuilder = supabase.from('work_sites').insert(sitesToInsert);
            if (typeof (insertBuilder as any)?.select === 'function') {
                const { data: sitesData, error: sitesError } = await (insertBuilder as any).select();
                if (sitesError) throw sitesError;
                if (sitesData) {
                    insertedWorkSites = (sitesData as any[]).map(mapWorkSiteRow);
                }
            } else {
                const { error: sitesError } = await insertBuilder;
                if (sitesError) throw sitesError;
            }
        }

        return {
            ...client,
            id: newClient.id,
            companyId,
            workSites: insertedWorkSites.length > 0 ? insertedWorkSites : (client.workSites || [])
        };
    },

    async addWorkSite(clientId: string, site: WorkSite): Promise<WorkSite> {
        // Guarda defensiva: comprobar si el cliente ya tiene una Dirección única registrada
        const { data: existingSites, error: checkError } = await supabase
            .from('work_sites')
            .select('id, name, is_unique_address')
            .eq('client_id', clientId);

        if (!checkError && existingSites && existingSites.length > 0) {
            const hasUnique = existingSites.some(s => 
                s.is_unique_address === true || 
                isUniqueAddressSite({ name: s.name, isUniqueAddress: s.is_unique_address } as any)
            );
            if (hasUnique) {
                throw new Error('Este cliente ya tiene una Dirección única registrada. No se pueden añadir más obras.');
            }
        }

        // Si la nueva obra se pretende marcar como única, verificar que el cliente no tenga obras previas
        const isNewSiteUnique = Boolean(site.isUniqueAddress || isUniqueAddressSite(site));
        if (isNewSiteUnique && existingSites && existingSites.length > 0) {
            throw new Error('Este cliente ya tiene obras registradas. No se puede añadir como Dirección única.');
        }

        let finalCompanyId = site.companyId;
        if (!finalCompanyId) {
            try {
                const { data: clientData } = await supabase
                    .from('clients')
                    .select('company_id')
                    .eq('id', clientId)
                    .maybeSingle();
                if (clientData?.company_id) {
                    finalCompanyId = clientData.company_id;
                }
            } catch (err) {
                logger.warn('Could not resolve company_id from client in addWorkSite', err);
            }
        }

        const hasStructured = Boolean(site.locationDetails?.street || site.locationDetails?.city || site.locationDetails?.province || site.locationDetails?.zipCode);
        const parsed = (!hasStructured && site.address) ? parseLegacyAddress(site.address) : {};
        const loc = {
            street: site.locationDetails?.street || parsed.street || null,
            number: site.locationDetails?.number || parsed.number || null,
            extra: site.locationDetails?.extra || parsed.extra || null,
            zipCode: site.locationDetails?.zipCode || parsed.zipCode || null,
            city: site.locationDetails?.city || parsed.city || null,
            province: site.locationDetails?.province || parsed.province || null
        };

        const insertBuilder = supabase.from('work_sites').insert({
            id: site.id && !site.id.startsWith('ws-') ? site.id : undefined,
            client_id: clientId,
            company_id: finalCompanyId,
            is_unique_address: isNewSiteUnique,
            name: site.name,
            address: site.address,
            street: loc.street,
            number: loc.number,
            extra: loc.extra,
            zip_code: loc.zipCode,
            city: loc.city,
            province: loc.province,
            location_details: loc
        });

        let insertedSite = null;
        if (typeof (insertBuilder as any)?.select === 'function') {
            const { data, error } = await (insertBuilder as any).select().single();
            if (error) throw error;
            insertedSite = data;
        } else {
            const { error } = await insertBuilder;
            if (error) throw error;
        }

        return insertedSite ? mapWorkSiteRow(insertedSite) : {
            ...site,
            companyId: finalCompanyId,
        };
    },

    async updateClient(client: Client): Promise<void> {
        // Validación de unicidad de nombre de cliente al actualizar
        const { data: existing, error: searchError } = await supabase
            .from('clients')
            .select('id')
            .eq('company_id', client.companyId)
            .ilike('name', client.name.trim())
            .neq('id', client.id)
            .limit(1);
        
        if (!searchError && existing && existing.length > 0) {
            throw new Error(`Ya existe otro cliente con el nombre "${client.name.trim()}" en esta empresa.`);
        }

        // Update basic client data
        const { data, error } = await supabase
            .from('clients')
            .update({
                name: client.name,
                email: client.email,
                phone: client.phone
            })
            .eq('id', client.id)
            .select();

        if (error) throw error;
        if (!data || data.length === 0) {
            throw new Error("Error: No se pudo actualizar el cliente. Verifique permisos.");
        }

        // Update work sites non-destructively with cascade sync
        if (client.workSites) {
            const parentCompanyId = data[0].company_id;
            const currentSiteIds = client.workSites.map(ws => ws.id).filter(Boolean);

            // Fetch previous state of work_sites for this client to detect renames and deletions
            const { data: previousSites } = await supabase
                .from('work_sites')
                .select('id, name, is_unique_address')
                .eq('client_id', client.id);

            const oldNamesById = new Map<string, string>();
            (previousSites || []).forEach(ps => {
                if (ps.id && ps.name) oldNamesById.set(ps.id, ps.name);
            });

            // Step 1: Anti-delete guard and removal of discarded sites
            const sitesToDelete = (previousSites || []).filter(ps => !currentSiteIds.includes(ps.id));
            if (sitesToDelete.length > 0) {
                const toDeleteIds = sitesToDelete.map(s => s.id);
                const { count: linkedOrdersCount } = await supabase
                    .from('work_orders')
                    .select('id', { count: 'exact', head: true })
                    .in('work_site_id', toDeleteIds);

                if (linkedOrdersCount && linkedOrdersCount > 0) {
                    throw new Error('No puedes eliminar una dirección u obra que tiene partes de trabajo vinculados. Desmarca "Dirección única" si deseas convertirla en obra normal.');
                }
            }

            if (currentSiteIds.length > 0) {
                const { error: deleteError } = await supabase
                    .from('work_sites')
                    .delete()
                    .eq('client_id', client.id)
                    .not('id', 'in', `(${currentSiteIds.join(',')})`);

                if (deleteError) {
                    logger.error('Error deleting removed work sites', deleteError);
                    throw new Error('No se pudieron eliminar las obras descartadas');
                }
            } else {
                // All sites removed
                const { error: deleteError } = await supabase
                    .from('work_sites')
                    .delete()
                    .eq('client_id', client.id);

                if (deleteError) {
                    logger.error('Error deleting all work sites', deleteError);
                    throw new Error('No se pudieron eliminar las obras');
                }
            }

            // Step 2: Upsert current work sites (preserving IDs and FK relations)
            if (client.workSites.length > 0) {
                const sitesToUpsert = client.workSites.map(ws => {
                    const isUnique = Boolean(ws.isUniqueAddress || isUniqueAddressSite(ws));
                    const hasStructured = Boolean(ws.locationDetails?.street || ws.locationDetails?.city || ws.locationDetails?.province || ws.locationDetails?.zipCode);
                    const parsed = (!hasStructured && ws.address) ? parseLegacyAddress(ws.address) : {};
                    const loc = {
                        street: ws.locationDetails?.street || parsed.street || null,
                        number: ws.locationDetails?.number || parsed.number || null,
                        extra: ws.locationDetails?.extra || parsed.extra || null,
                        zipCode: ws.locationDetails?.zipCode || parsed.zipCode || null,
                        city: ws.locationDetails?.city || parsed.city || null,
                        province: ws.locationDetails?.province || parsed.province || null
                    };

                    return {
                        id: ws.id,
                        client_id: client.id,
                        company_id: parentCompanyId,
                        is_unique_address: isUnique,
                        name: ws.name,
                        address: ws.address,
                        street: loc.street,
                        number: loc.number,
                        extra: loc.extra,
                        zip_code: loc.zipCode,
                        city: loc.city,
                        province: loc.province,
                        location_details: loc
                    };
                });

                const { error: upsertError } = await supabase
                    .from('work_sites')
                    .upsert(sitesToUpsert);

                if (upsertError) {
                    logger.error('Error upserting work sites', upsertError);
                    throw new Error(`No se pudieron guardar las obras: ${upsertError.message}`);
                }

                // Step 3: Cascade rename to work_orders
                // Update work_orders title for all orders linked to each work site (except unique address)
                for (const ws of client.workSites) {
                    if (ws.id && ws.name && !ws.isUniqueAddress) {
                        const oldName = oldNamesById.get(ws.id);

                        // 1. Direct update by work_site_id
                        const { error: woError } = await supabase
                            .from('work_orders')
                            .update({ title: ws.name })
                            .eq('work_site_id', ws.id);

                        if (woError) {
                            logger.warn(`[ClientService] Could not cascade title update to work_orders for site ${ws.id}:`, woError);
                        }

                        // 2. Cascade by old name (for legacy/orphaned orders where work_site_id might be null or matching old title)
                        if (oldName && oldName.trim().toLowerCase() !== ws.name.trim().toLowerCase()) {
                            await supabase
                                .from('work_orders')
                                .update({ 
                                    title: ws.name,
                                    work_site_id: ws.id 
                                })
                                .eq('client_id', client.id)
                                .eq('title', oldName);
                        }
                    }
                }

                // Step 4: Self-healing for orphaned work_orders of this client:
                // Match remaining unlinked orders of this client to the existing sites by name
                for (const ws of client.workSites) {
                    if (ws.id && ws.name) {
                        await supabase
                            .from('work_orders')
                            .update({ work_site_id: ws.id })
                            .eq('client_id', client.id)
                            .eq('title', ws.name)
                            .is('work_site_id', null);
                    }
                }

                // If the client has only 1 work site, link any remaining work_orders with null work_site_id
                if (client.workSites.length === 1 && client.workSites[0].id) {
                    const singleSite = client.workSites[0];
                    await supabase
                        .from('work_orders')
                        .update({ 
                            work_site_id: singleSite.id,
                            title: singleSite.name 
                        })
                        .eq('client_id', client.id)
                        .is('work_site_id', null);
                }
            }
        }
    },

    async deleteClient(clientId: string): Promise<void> {
        const { error } = await supabase.from('clients').delete().eq('id', clientId);
        if (error) throw error;
    }
};
